import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, LinearProgress, Tab, Tabs } from '@mui/material';
import { tabsClasses } from '@mui/material/Tabs';
import deepmerge from 'deepmerge';

import { StorePromotionTypeEnum } from '../../../../core/enums/price/store_promotion';
import StorePromotionSettingsList from './store_promotion_settings_list';
import { getPodsNotificationData, getStorePromotionSettingsNotificationData } from '../../../../core/utils/notification_utils';
import useNotification from '../../../../core/hooks/use_notification';
import { getDefaultPodsPaginationQueryValues } from '../../../../core/utils/api_utils';
import { getPodsAction, getPodsState } from '../../../../features/pod/get_pods';
import PaginationControls from '../../../common/pagination_controls';
import { getStorePromotionSettingsAction, getStorePromotionSettingsState } from '../../../../features/price/get_store_promotion_settings';
import PageHeader from '../../../common/page_header';

const initialQueryParams = {
    ...getDefaultPodsPaginationQueryValues
};

const StorePromotionSettingsPage = () => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    const getPods = useSelector(getPodsState);
    const getStorePromotionSettings = useSelector(getStorePromotionSettingsState);

    const [queryParams, setQueryParams] = useState(initialQueryParams);
    const [tab, setTab] = useState(0);
    const [type, setType] = useState(StorePromotionTypeEnum.CHEAPER_PRODUCT_FOR_NEW_USERS.value);

    const refreshStorePromotionSettingList = useCallback(() => {
        dispatch(getStorePromotionSettingsAction(type))
            .then(response => showErrorMessage(getStorePromotionSettingsNotificationData(response)));
    }, [dispatch, showErrorMessage, type]);

    useEffect(() => {
        refreshStorePromotionSettingList();
    }, [dispatch, refreshStorePromotionSettingList, showErrorMessage]);

    useEffect(() => {
        dispatch(getPodsAction(queryParams))
            .then(response => showErrorMessage(getPodsNotificationData(response)));
    }, [dispatch, queryParams, showErrorMessage]);

    const onTabChange = (tabValue) => {
        setTab(tabValue);
        setQueryParams(prevState => deepmerge(prevState, { page: 0 }));

        if (tabValue === 0) {
            setType(StorePromotionTypeEnum.CHEAPER_PRODUCT_FOR_NEW_USERS.value);
        } else if (tabValue === 1) {
            setType(StorePromotionTypeEnum.GREEN_PROMOTION_FOR_NEW_USERS.value);
        } else if (tabValue === 2) {
            setType(StorePromotionTypeEnum.GREEN_PROMOTION_ONE_FREE.value);
        } else {
            setType(StorePromotionTypeEnum.QUANTITATIVE_PROMOTION_ONE_CHEAPER.value);
        }
    };

    const renderView = () => {
        if (getPods?.loading || getStorePromotionSettings?.loading) {
            return (
                <Box mt={ 6 }>
                    <LinearProgress/>
                </Box>
            );
        }

        if (getPods?.data?.content && getStorePromotionSettings?.data) {
            return (
                <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
                    <StorePromotionSettingsList
                        refreshList={ refreshStorePromotionSettingList }
                        type={ type }
                    />
                    <Box sx={ { mt: 2, display: 'flex', justifyContent: 'center' } }>
                        <PaginationControls
                            currentPage={ getPods?.data?.pageable?.pageNumber }
                            totalPages={ getPods?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                        />
                    </Box>
                </Box>
            );
        }

        return <></>;
    };

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3, mb: 4 } }>
            <PageHeader
                title="Ustawienia promocji sklepowych"
            />

            <Box sx={ { borderBottom: 1, borderColor: 'divider' } }>
                <Tabs
                    value={ tab }
                    onChange={ (_, value) => onTabChange(value) }
                    variant="scrollable"
                    scrollButtons
                    sx={ {
                        [`& .${ tabsClasses.scrollButtons }`]: {
                            '&.Mui-disabled': { opacity: 0.3 },
                        },
                    } }
                >
                    <Tab label={ StorePromotionTypeEnum.CHEAPER_PRODUCT_FOR_NEW_USERS.label }/>
                    <Tab label={ StorePromotionTypeEnum.GREEN_PROMOTION_FOR_NEW_USERS.label }/>
                    <Tab label={ StorePromotionTypeEnum.GREEN_PROMOTION_ONE_FREE.label }/>
                    <Tab label={ StorePromotionTypeEnum.QUANTITATIVE_PROMOTION_ONE_CHEAPER.label }/>
                </Tabs>
            </Box>

            { renderView() }
        </Box>
    );
};

export default StorePromotionSettingsPage;